import { useCallback, useLayoutEffect, useState } from 'react'

/**
 * Measures the rendered width of a DOM node and keeps it current across
 * resizes. Returns a callback ref so the measurement re-runs whenever the
 * node mounts or unmounts (for example when a tab switch swaps the element).
 *
 * @example
 *   const [ref, width] = useElementWidth()
 *   <div ref={ref} />
 *
 * @returns {[(el: HTMLElement | null) => void, number]} `[ref, width]`.
 *   `width` is 0 until the node has been measured.
 */
export const useElementWidth = () => {
    const [node, setNode] = useState(null)
    const [width, setWidth] = useState(0)
    const ref = useCallback((el) => setNode(el), [])

    useLayoutEffect(() => {
        if (!node) return undefined
        const update = () => setWidth(node.clientWidth)
        update()

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update)
            return () => window.removeEventListener('resize', update)
        }

        const observer = new ResizeObserver(update)
        observer.observe(node)
        return () => observer.disconnect()
    }, [node])

    return [ref, width]
}

export default useElementWidth
